import {
  ACCESS_TOKEN_COOKIE,
  NEXAR_PC_USER_ID,
  NEXAR_USER_ID,
  PC_ACCESS_TOKEN_COOKIE,
  PC_REFRESH_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
} from "./consts";
import CONFIG from "../../../core/consts/config";
import jwtDecode from "jwt-decode";
import { deleteValue, getValue, setValue } from "./storage";
import {
  LoginResponse,
  PCTokenResponse,
  ValidatePhoneResponse,
} from "../../../contracts/nexar-api/types";
import MD5 from "crypto-js/md5";
import { HttpError } from "../../../core/errors/errors";

function clearStorage() {
  deleteValue(ACCESS_TOKEN_COOKIE);
  deleteValue(REFRESH_TOKEN_COOKIE);
  deleteValue(PC_ACCESS_TOKEN_COOKIE);
  deleteValue(PC_REFRESH_TOKEN_COOKIE);
  deleteValue(NEXAR_PC_USER_ID);
  deleteValue(NEXAR_USER_ID);
}
export async function getOrUpdateRxdaemonAccessToken(): Promise<string | null> {
  const accessToken = getValue(ACCESS_TOKEN_COOKIE);

  if (!accessToken) {
    return null;
  }

  if (!isTokenExpired(accessToken)) {
    return accessToken;
  } else {
    const credentials = await refreshRxdaemonTokens();
    if (!credentials) {
      clearStorage();
      throw Error("Failed to refresh rxdaemon token");
    }

    setValue(ACCESS_TOKEN_COOKIE, credentials.accessToken);
    setValue(REFRESH_TOKEN_COOKIE, credentials.refresh_token);

    return credentials.access_token;
  }
}

export async function getOrUpdatePersonalCloudAccessToken(): Promise<
  string | null
> {
  const pcAccessToken = getValue(PC_ACCESS_TOKEN_COOKIE);

  if (!pcAccessToken) {
    return null;
  }

  if (!isTokenExpired(pcAccessToken)) {
    return pcAccessToken;
  } else {
    const credentials = await refreshPersonalCloudTokens();
    if (!credentials) {
      clearStorage();
      throw Error("Failed to refresh PC token");
    }

    setValue(PC_ACCESS_TOKEN_COOKIE, credentials.accessToken);
    setValue(PC_REFRESH_TOKEN_COOKIE, credentials.refreshToken);
    setValue(NEXAR_PC_USER_ID, credentials.userId);

    return credentials.accessToken;
  }
}

function isTokenExpired(token: string) {
  if (token) {
    let now = new Date().getTime();

    try {
      let decoded: { exp: number } = jwtDecode(token);
      return decoded.exp * 1000 - now < 3000;
    } catch (e) {}
  }
  return true;
}

function getRxdaemonRefreshToken() {
  return getValue(REFRESH_TOKEN_COOKIE);
}
async function refreshRxdaemonTokens() {
  const refreshToken = getRxdaemonRefreshToken();

  if (!refreshToken || isTokenExpired(refreshToken)) {
    return null;
  }

  return fetch(`${CONFIG.AUTH_SERVER}/auth/credentials`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then((resp) => resp.json())
    .then((resp) => (!resp || !resp.access_token ? null : resp))
    .catch((err) => {
      throw err;
    });
}
async function refreshPersonalCloudTokens() {
  const pcRefreshToken = getValue(PC_REFRESH_TOKEN_COOKIE);
  if (!pcRefreshToken || isTokenExpired(pcRefreshToken)) {
    return null;
  }

  let rxdaemonAccessToken;
  try {
    rxdaemonAccessToken = await getOrUpdateRxdaemonAccessToken();
  } catch (e) {
    throw Error("Failed to get or refresh rxdaemon access token");
  }

  if (!rxdaemonAccessToken) {
    throw Error("Can't get rxdaemon access token");
  }

  return await getPersonalCloudTokensByRxdaemonAccessToken({
    rxdaemonAccessToken: rxdaemonAccessToken,
  });
}

const getPersonalCloudTokensByRxdaemonAccessToken = async ({
  rxdaemonAccessToken,
}: {
  rxdaemonAccessToken: string;
}): Promise<PCTokenResponse> => {
  const res = await fetch(`${CONFIG.PERSONAL_CLOUD_SERVER}/get-user-token`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${rxdaemonAccessToken}`,
    },
  });
  const response = (await res.json()) as LoginResponse;

  return {
    refreshToken: response.payload.user_token.refresh_token,
    accessToken: response.payload.user_token.session_token,
    userId: response.payload.user_id.id,
  };
};

export async function validateOtpCodeAndLogin(
  countryCode: string,
  phoneNumber: string,
  digits: string,
  clientId: string
): Promise<void> {
  const phoneWithCountryCode = encodeURIComponent(countryCode) + phoneNumber;
  clearStorage();

  let res;
  try {
    res = await fetch(
      `${CONFIG.AUTH_SERVER}/auth/sms?client_id=${clientId}&phone_number=${phoneWithCountryCode}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Nexar ${MD5(
            digits
          )} FUTURE_SIGNATURE_NOT_IMPLEMENTED_YET`,
        },
      }
    );
  } catch (err) {
    throw new Error("Failed to validate phone number");
  }
  const data = await res.json();
  if (typeof data.error !== "undefined") {
    throw new Error(data.error);
  } else {
    const dataSuccess = data as ValidatePhoneResponse;
    const personalCloudRes = await getPersonalCloudTokensByRxdaemonAccessToken({
      rxdaemonAccessToken: dataSuccess.BearerAccessToken,
    });

    setValue(ACCESS_TOKEN_COOKIE, dataSuccess.BearerAccessToken);
    setValue(REFRESH_TOKEN_COOKIE, dataSuccess.BearerRefreshToken);
    setValue(NEXAR_USER_ID, dataSuccess.NexarUserId);
    setValue(PC_ACCESS_TOKEN_COOKIE, personalCloudRes.accessToken);
    setValue(PC_REFRESH_TOKEN_COOKIE, personalCloudRes.refreshToken);
    setValue(NEXAR_PC_USER_ID, personalCloudRes.userId);
  }
}

export async function postPcAuthenticated(path: string, data: any) {
  const pcToken = await getOrUpdatePersonalCloudAccessToken();

  const res = await fetch(`${CONFIG.PERSONAL_CLOUD_SERVER}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${pcToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let body;
    try {
      body = await res.text();
    } catch (e) {}
    throw new HttpError(res.status, res.statusText, body);
  }
  return res.json();
}

export async function fetchRxdaemonAuthenticated(
  method: "GET",
  path: string,
  data?: any,
  additionalHeaders?: any
) {
  const rxdaemonToken = await getOrUpdateRxdaemonAccessToken();

  const requestInit: RequestInit = {
    method,
    headers: {
      Authorization: `Bearer ${rxdaemonToken}`,
      "Content-Type": "application/json",
      ...additionalHeaders,
    },
  };
  if (data) {
    requestInit.body = JSON.stringify(data);
  }

  const res = await fetch(`${CONFIG.AUTH_SERVER}${path}`, requestInit);
  if (!res.ok) {
    let body;
    try {
      body = await res.text();
    } catch (e) {}
    throw new HttpError(res.status, res.statusText, body);
  }
  return res.json();
}

export async function logout() {
  try {
    const refreshToken = getRxdaemonRefreshToken();
    await fetchRxdaemonAuthenticated("GET", `/auth/logout`, undefined, {
      refresh_token: refreshToken,
    });
  } catch (e) {
    console.error(e);
  } finally {
    clearStorage();
    return Promise.resolve();
  }
}
